// Canvas text measurement and rendering service
// Replaces CanvasText.js with: cached font metrics, binary search line wrapping, no DOM access

// Hair space character for text justification
const HAIR_SPACE = '\u200a';

// --- Types ---

export interface FontConfig {
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  fontVariant?: string;
  lineHeight: number;
}

export interface AlignConfig {
  align?: string;      // 'left' | 'center' | 'right'
  vAlign?: string;     // 'top' | 'center' | 'bottom'
  justify?: boolean;
}

export interface TextBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface FontMetrics {
  fontContentArea: number;
  fontActualSize: number;
  baseline: number;
  descent: number;
  lineGap: number;
}

interface ComputedFontMetrics extends FontMetrics {
  lineHeightOffset: number;
  charHeight: number;
}

// --- Font metrics cache ---

const METRICS_CACHE = new Map<string, FontMetrics>();

function buildFontString(config: FontConfig): string {
  return `${config.fontStyle} ${config.fontVariant ?? ''} ${config.fontWeight} ${config.fontSize}px ${config.fontFamily}`;
}

function buildCacheKey(config: FontConfig): string {
  return `${config.fontFamily}__${config.fontWeight}__${config.fontStyle}__${config.fontSize}`;
}

function getFontMetrics(ctx: CanvasRenderingContext2D, config: FontConfig): FontMetrics {
  const key = buildCacheKey(config);
  const cached = METRICS_CACHE.get(key);
  if (cached) return cached;

  const prevBaseline = ctx.textBaseline;
  const prevFont = ctx.font;

  ctx.textBaseline = 'bottom';
  ctx.font = buildFontString(config);

  // Use native fontBoundingBox (supported in all modern browsers)
  const fullMetrics = ctx.measureText('Mygtlf])');
  const fontContentArea = fullMetrics.fontBoundingBoxAscent + fullMetrics.fontBoundingBoxDescent;
  const fontActualSize = fullMetrics.actualBoundingBoxAscent + fullMetrics.actualBoundingBoxDescent;
  const baseline = fullMetrics.actualBoundingBoxAscent;

  const xHeight = ctx.measureText('v');
  const descender = ctx.measureText('y');
  const descent =
    (descender.actualBoundingBoxAscent + descender.actualBoundingBoxDescent) -
    (xHeight.actualBoundingBoxAscent + xHeight.actualBoundingBoxDescent);

  const lineGap = fontContentArea - fontActualSize;

  ctx.textBaseline = prevBaseline;
  ctx.font = prevFont;

  const metrics: FontMetrics = { fontContentArea, fontActualSize, baseline, descent, lineGap };
  METRICS_CACHE.set(key, metrics);
  return metrics;
}

function getComputedMetrics(ctx: CanvasRenderingContext2D, config: FontConfig): ComputedFontMetrics {
  const base = getFontMetrics(ctx, config);
  let lineHeightOffset = config.lineHeight === config.fontSize ? 0 : config.lineHeight;
  if (config.lineHeight !== config.fontSize) {
    lineHeightOffset = parseFloat(((lineHeightOffset - config.fontSize) / 2).toFixed(2));
  }
  const charHeight = lineHeightOffset * 2 + base.fontContentArea;
  return { ...base, lineHeightOffset, charHeight };
}

// --- Line wrapping ---

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number | null,
  fontString: string,
  justify: boolean,
): string[] {
  ctx.font = fontString;

  // Decode the title formatting tokens the Map Viewer renders as HTML:
  // &nbsp; → a real non-breaking space (also a non-wrapping point), &shy; → an
  // optional break that shows a hyphen, <br> → a hard line break.
  const segments = text
    .replaceAll('&nbsp;', '\u00A0')
    .replaceAll('&shy;', ' -<br>')
    .split('<br>');
  if (!maxWidth) return segments;

  const spaceWidth = justify ? ctx.measureText(HAIR_SPACE).width : 0;
  const lines: string[] = [];

  for (const segment of segments) {
    if (ctx.measureText(segment).width <= maxWidth) {
      lines.push(segment);
      continue;
    }

    let remaining = segment;
    while (ctx.measureText(remaining).width > maxWidth) {
      // Binary search for the maximum number of chars that fit
      let lo = 1;
      let hi = remaining.length;
      while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (ctx.measureText(remaining.substring(0, mid)).width <= maxWidth) {
          lo = mid;
        } else {
          hi = mid - 1;
        }
      }

      // lo is the last char index that fits; back up to word boundary
      let breakAt = lo;
      if (remaining[breakAt] !== ' ') {
        let wordBreak = breakAt;
        while (wordBreak > 0 && remaining[wordBreak] !== ' ') {
          wordBreak--;
        }
        if (wordBreak > 0) breakAt = wordBreak;
      }

      let line = remaining.substring(0, breakAt);
      if (justify) {
        line = justifyLine(ctx, line, spaceWidth, HAIR_SPACE, maxWidth);
      }
      lines.push(line);
      remaining = remaining.substring(breakAt);
    }

    if (remaining.length > 0) {
      lines.push(remaining);
    }
  }

  return lines;
}

function justifyLine(
  ctx: CanvasRenderingContext2D,
  line: string,
  spaceWidth: number,
  spaceChar: string,
  width: number,
): string {
  const text = line.trim();
  const lineWidth = ctx.measureText(text).width;
  const nbSpaces = text.split(/\s+/).length - 1;
  const nbSpacesToInsert = Math.floor((width - lineWidth) / spaceWidth);

  if (nbSpaces <= 0 || nbSpacesToInsert <= 0) return text;

  const nbSpacesMinimum = Math.floor(nbSpacesToInsert / nbSpaces);
  let extraSpaces = nbSpacesToInsert - nbSpaces * nbSpacesMinimum;

  const baseSpaces = spaceChar.repeat(nbSpacesMinimum);

  return text.replace(/\s+/g, (match) => {
    const allSpaces = extraSpaces > 0 ? baseSpaces + spaceChar : baseSpaces;
    extraSpaces--;
    return match + allSpaces;
  });
}

// --- Public API ---

export function measureTextDimensions(
  ctx: CanvasRenderingContext2D,
  text: string | null,
  config: FontConfig,
  maxWidth?: number | null,
): { textWidth: number; textHeight: number } {
  if (!text) return { textWidth: 0, textHeight: 0 };

  const fontString = buildFontString(config);
  const textArray = wrapText(ctx, text, maxWidth ?? null, fontString, false);
  const { charHeight } = getComputedMetrics(ctx, config);

  const rowWidths: number[] = [];
  ctx.font = fontString;
  for (const row of textArray) {
    rowWidths.push(ctx.measureText(row).width);
  }
  const longestLine = Math.max(...rowWidths);
  const textWidth = maxWidth || longestLine;

  return {
    textWidth: Math.ceil(textWidth),
    textHeight: charHeight * textArray.length,
  };
}

export function drawText(
  ctx: CanvasRenderingContext2D,
  text: string | null,
  bounds: TextBounds,
  config: FontConfig,
  alignCfg: AlignConfig = {},
  debug: boolean = false,
): void {
  if (!text || config.fontSize === 0) return;

  const align = alignCfg.align ?? 'center';
  const vAlign = alignCfg.vAlign ?? 'center';
  const justify = alignCfg.justify ?? false;

  const fontString = buildFontString(config);
  const textArray = wrapText(ctx, text, bounds.width || null, fontString, justify);
  if (!textArray || textArray.length === 0) return;

  const metrics = getComputedMetrics(ctx, config);
  const { fontActualSize, descent, lineGap, lineHeightOffset, charHeight } = metrics;

  const textHeight = charHeight * textArray.length;
  const vheight = charHeight * (textArray.length - 1);
  const { x, y, width, height } = bounds;

  // Horizontal anchor
  let textAnchor: number;
  if (align === 'right') {
    textAnchor = x + width;
    ctx.textAlign = 'right';
  } else if (align === 'left') {
    textAnchor = x;
    ctx.textAlign = 'left';
  } else {
    textAnchor = x + width / 2;
    ctx.textAlign = 'center';
  }

  // Baseline offset for first line
  const charBaselineOffset = fontActualSize - descent + lineGap / 2 + lineHeightOffset;
  const lineStep = charBaselineOffset + descent + lineGap / 2 + lineHeightOffset;

  // Vertical position
  let txtY: number;
  if (vAlign === 'top') {
    txtY = y + charBaselineOffset;
  } else if (vAlign === 'bottom') {
    txtY = y + height - vheight;
  } else {
    txtY = y + (height - textHeight) / 2 + charBaselineOffset;
  }

  // Set font for rendering
  ctx.font = fontString;

  // Render lines
  for (const line of textArray) {
    ctx.fillText(line, textAnchor, txtY);
    txtY += lineStep;
  }

  // Debug visualization
  if (debug) {
    const xEnd = x + width;
    const yEnd = y + height;
    ctx.lineWidth = 3;
    ctx.strokeStyle = 'rgba(0, 144, 158, .5)';
    ctx.strokeRect(x, y, width, height);

    ctx.lineWidth = 2;
    ctx.strokeStyle = '#f6d743';
    ctx.beginPath();
    ctx.moveTo(textAnchor, y);
    ctx.lineTo(textAnchor, yEnd);
    ctx.stroke();

    ctx.strokeStyle = '#ff6363';
    ctx.beginPath();
    const debugY = vAlign === 'bottom' ? yEnd : vAlign === 'top' ? y : y + height / 2;
    ctx.moveTo(x, debugY);
    ctx.lineTo(xEnd, debugY);
    ctx.stroke();
  }
}

// Cleanup: remove legacy DOM measurement element if present
export function cleanupLegacyMeasureElement(): void {
  const el = document.getElementById('__textMeasure');
  if (el) el.remove();
}
